import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import {
  ClearMemoDetail,
  GetVerificationAction,
  ResetOnApprove,
  SetLoaData,
  SetOnApprove,
  SetValidationError,
  ValidationFieldType,
  isSave,
} from './pre-approve.actions';
import { PreApproveStateModel, defaults } from './pre-approve.model';
import { PreApproveService } from 'src/app/modules/s11-e-signature/shared/pre-approve.service';
import { VerificationAction } from 'src/app/modules/s11-e-signature/shared/pre-approve-models';
import { MemoLoaPreview } from '@shared/models/memo.model';

@State<PreApproveStateModel>({
  name: 'preApprove',
  defaults,
})
@Injectable()
export class PreApproveState {
  constructor(private preApproveService: PreApproveService) {}
  @Selector()
  static getSaveAction(state: PreApproveStateModel): string | null {
    return state.saveAction;
  }

  @Selector()
  static getLoaLevel(
    state: PreApproveStateModel,
  ): MemoLoaPreview[] | null {
    return state.loaLevel?.length ? state.loaLevel : null;
  }

  @Selector()
  static getDynamicData(state: PreApproveStateModel) {
    return state.onApprove;
  }

  @Selector()
  static getVerificationAction(
    state: PreApproveStateModel,
  ): VerificationAction | null {
    return state.verificationAction;
  }

  @Selector()
  static getValidationError(
    state: PreApproveStateModel,
  ): (fieldType: ValidationFieldType) => boolean {
    return (fieldType: ValidationFieldType) => {
      switch (fieldType) {
        case 'phone':
          return state.phoneValidationError;
        case 'name':
          return state.nameValidationError;
        case 'lastName':
          return state.lastNameValidationError;
        default:
          return false;
      }
    };
  }

  @Action(ClearMemoDetail)
  clearMemoDetail(ctx: StateContext<PreApproveStateModel>) {
    ctx.patchState({
      memoDetail: null,
    });
  }

  @Action(isSave)
  isSave(ctx: StateContext<PreApproveStateModel>, action: isSave) {
    ctx.patchState({
      saveAction: action.payload,
    });
  }

  @Action(SetLoaData)
  setLoaData(
    ctx: StateContext<PreApproveStateModel>,
    action: SetLoaData,
  ) {
    ctx.patchState({
      loaLevel: action.payload,
    });
  }

  @Action(SetOnApprove)
  setOnApprove(
    ctx: StateContext<PreApproveStateModel>,
    action: SetOnApprove,
  ) {
    ctx.patchState({ onApprove: action.payload });
  }

  @Action(ResetOnApprove)
  resetOnApprove(ctx: StateContext<PreApproveStateModel>) {
    ctx.patchState({ onApprove: null }); // or {} or [] based on your initial shape
  }

  @Action(SetValidationError)
  setValidationError(
    ctx: StateContext<PreApproveStateModel>,
    action: SetValidationError,
  ) {
    switch (action.fieldType) {
      case 'phone':
        ctx.patchState({
          phoneValidationError: action.payload,
        });
        break;
      case 'name':
        ctx.patchState({
          nameValidationError: action.payload,
        });
        break;
      case 'lastName':
        ctx.patchState({
          lastNameValidationError: action.payload,
        });
        break;
    }
  }

  @Action(GetVerificationAction)
  getVerificationAction(
    ctx: StateContext<PreApproveStateModel>,
    action: GetVerificationAction,
  ) {
    return this.preApproveService
      .checkVerificationActionByType(action.id, action.memoType)
      .pipe(
        tap((result: VerificationAction) => {
          ctx.patchState({
            verificationAction: result,
          });
        }),
      );
  }
}
